// The stable release bucket used by Pulse's publisher and installed updaters.
// Do not accept a manifest-provided origin, path, query or installer filename.
export const PULSE_RELEASES = 'https://ddijknnubevgvmbrufap.supabase.co/storage/v1/object/public/halo-pulse-releases'
export const PULSE_MANIFEST = `${PULSE_RELEASES}/latest.json`
export type PulsePlatform = 'mac' | 'windows'
export type PulseInstaller = { url: string; version: string }
export type InstallerState = PulseInstaller | 'unavailable' | null

function record(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export function pulseInstallers(manifest: unknown): Record<PulsePlatform, PulseInstaller | null> {
  const missing = { mac: null, windows: null }
  if (!record(manifest) || typeof manifest.version !== 'string' || !/^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/.test(manifest.version) || !record(manifest.platforms)) return missing
  const version = manifest.version
  const base = `${PULSE_RELEASES}/${version}`
  const mac = manifest.platforms['darwin-aarch64']
  const windows = manifest.platforms['windows-x86_64']
  // Pulse publishes a universal DMG alongside the universal updater archive.
  // Windows' NSIS installer is also its updater payload, so use its exact URL.
  return {
    mac: record(mac) && mac.url === `${base}/halo-pulse-darwin-universal.app.tar.gz`
      ? { url: `${base}/halo-pulse-darwin-universal.dmg`, version } : null,
    windows: record(windows) && windows.url === `${base}/halo-pulse-windows-x86_64-setup.exe`
      ? { url: windows.url, version } : null,
  }
}

export async function loadPulseInstallers(update: (platform: PulsePlatform, value: InstallerState) => void, signal: AbortSignal): Promise<void> {
  let installers: ReturnType<typeof pulseInstallers>
  try {
    const response = await fetch(PULSE_MANIFEST, { cache: 'no-store', redirect: 'error', signal })
    if (!response.ok) throw new Error('Release manifest unavailable')
    installers = pulseInstallers(await response.json())
  } catch {
    update('mac', 'unavailable')
    update('windows', 'unavailable')
    return
  }
  await Promise.all((['mac', 'windows'] as const).map(async platform => {
    const installer = installers[platform]
    if (!installer) { update(platform, 'unavailable'); return }
    try {
      // Publication can be partial: a manifest pointer alone does not prove an
      // installer exists. Resolve each independently so one failure cannot hide
      // the other. Never download the binary merely to render the menu.
      const response = await fetch(installer.url, { method: 'HEAD', cache: 'no-store', redirect: 'error', signal })
      if (!response.ok || response.headers.get('content-length') === '0') throw new Error('Installer unavailable')
      const url = new URL(installer.url)
      url.searchParams.set('download', `HALO-Pulse-${installer.version}${platform === 'mac' ? '.dmg' : '-windows-x64-setup.exe'}`)
      update(platform, { ...installer, url: url.href })
    } catch { update(platform, 'unavailable') }
  }))
}

export function pulsePlatform(userAgent: string, maxTouchPoints = 0): PulsePlatform | null {
  // iPad desktop user agents can identify as a Mac; neither mobile OS runs Pulse.
  if (/iPhone|iPad|iPod|Android/i.test(userAgent) || (/Mac/i.test(userAgent) && maxTouchPoints > 1)) return null
  return /Mac/i.test(userAgent) ? 'mac' : /Windows/i.test(userAgent) ? 'windows' : null
}
